import { useRef } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';
import { buildExpressQuery } from '../queries/builders';

const getCancelToken = (callbackCancel) => {
    return new axios.CancelToken((cancel) => {
        callbackCancel(() => {
            cancel('Request canceled');
        });
    });
};

/**
 * Function to generate query key for react-query
 * Converts nested objects to serialized JSON strings
 * @param {{queryParams: any}} config
 */
export const getQueryKey = (queryParams) => {
    let queryKey = { ...queryParams };
    Object.keys(queryParams).forEach((key) => {
        let value = queryKey[key];
        if (!value) return '';
        if (moment.isMoment(value)) queryKey[key] = value.toISOString();
        if (typeof value !== 'string') queryKey[key] = JSON.stringify(value);
    });
    return queryKey;
};

/**
 * Curry function for building express axios requests
 * @param {{endpoint: string, params: any, body: any, options: import('axios').AxiosRequestConfig, method: import('axios').AxiosRequestConfig["method"]}} config
 */
export const useExpressQuery = ({ endpoint, params, body, options = {}, method = 'get' }) => {
    const request = async () => {
        const query = buildExpressQuery(endpoint, { params, body });
        if (typeof query === 'object') {
            const response = await axios[method](query.url, query.body, options);
            return response.data;
        }
        const response = await axios[method](query, options);

        return response.data;
    };

    return request;
};

/**
 * Executes axios request to express
 * @param {{endpoint: string, params: any, body: any, options: import('axios').AxiosRequestConfig, method: import('axios').AxiosRequestConfig["method"]}} config
 */
export const expressQuery = async ({ endpoint, params, body, options = {}, method = 'get' }) => {
    const query = buildExpressQuery(endpoint, { params, body });
    const response = await axios[method](query, options);

    return response.data;
};

/**
 * Executes axios mutation request to express
 * @param {{method: import('axios').AxiosRequestConfig["method"], endpoint: string, params: any, body: any, options: import('axios').AxiosRequestConfig}} config
 */
export const expressMutation = async ({ method = 'POST', endpoint, params, body, options = {}, queryBuilder = buildExpressQuery }) => {
    const request = queryBuilder(endpoint, { params, body });
    let { url, body: data } = request;
    if (!body) {
        url = request;
        data = undefined;
    }
    const response = await axios({
        method,
        url,
        data,
        ...options,
    });

    return response.data;
};

export default useExpressQuery;
